import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { LedgerRow, OffsetPage, RevenueReport } from '../_shared/types';
import { EMPTY_FILTERS, type LedgerFilters } from './filters';

/**
 * Two independent reads, never one composite, and the split is the platform's
 * rather than this surface's.
 *
 * **The aggregate band is `backend-analytics`'.** Money lives in exactly one
 * database, and the dashboard composition over it is the analytics service's job
 * — so the four figures and the plan split here are the **same payload the
 * console entry renders**, restated rather than recomputed. Two surfaces
 * reporting one datum with two values is drift; two paths to one source is not.
 *
 * **The record-level ledger is `backend-finance`'.** Its six columns are
 * assembled in the money service and nowhere else.
 *
 * Reading them separately is what gives the page its per-band error boundary: an
 * unreachable analytics service takes the figures down and leaves the ledger
 * usable, and the reverse holds too.
 */

export function revenueReportKey() {
  return key('analytics', 'report', 'revenue');
}

export function useRevenueReport() {
  return useQuery({
    queryKey: revenueReportKey(),
    queryFn: () => api.get<RevenueReport>('analytics', '/admin/reports/revenue'),
    // No period parameter is sent on this read, ever: no catalog row gives a
    // reporting surface a period, and the service answers `422` to one rather
    // than accepting a parameter that changes nothing.
    staleTime: STALE_TIME.list,
  });
}

/**
 * The page size the ledger walks in.
 *
 * Ten rather than the service's own twenty: this table is read against the
 * revenue tiles directly above it, and a page that runs past the fold puts the
 * figures and the rows they explain on two different screens. The service caps
 * the parameter at a hundred, so the console's choice is the binding one.
 */
export const LEDGER_PAGE_SIZE = 10;

/**
 * How many accounts one term may resolve to.
 *
 * The account register caps its own page at a hundred, and a hundred ids is a
 * request line of roughly 2.7 kB — inside every proxy's header budget. A term
 * matching more accounts than that is not a term anybody is searching with: the
 * operator is looking for *a* subscriber, and the answer to a single letter is
 * not a narrower ledger. Whether the resolve was cut is reported to the surface
 * rather than swallowed, so a page missing rows says why.
 */
const TERM_MATCH_CAP = 100;

/**
 * Keyed by ordinal **and by every facet**, because each combination is a
 * distinct list rather than a view of one cached one.
 *
 * The term is part of the key rather than resolved outside it: the account
 * resolve and the ledger read are one answer to one question, and caching the
 * second without the first would serve a narrowed page under the key the
 * unfiltered ledger reads from.
 */
export function subscriptionsKey(page?: number, filters?: LedgerFilters) {
  if (page === undefined) return key('finance', 'subscriptions');
  return key('finance', 'subscriptions', { page, ...filters });
}

/**
 * The ledger — narrowed, paged, and with each row's subscriber named.
 *
 * **The person facet is resolved here, not in the money service.**
 * `backend-finance` holds a `user_id` and nothing else about the account:
 * identity is `backend-auth`'s, one database per service, and no cross-service
 * SQL. So a term the operator types is answered in two hops — the account
 * register resolves it to a set of ids, and the ledger is narrowed to that set.
 * The console is the composition point for the same reason it already was on
 * this surface: it is the only place both sides are readable.
 *
 * **A term matching nobody is answered without a request.** An empty account set
 * is an empty ledger by definition, and sending the facet with nothing in it
 * would ask the money service a question whose answer is already in hand — which
 * is why that service refuses an empty one.
 *
 * **The resolve pays for itself.** A roster row carries the display name and the
 * mobile number, so a searched page needs no per-account lookup at all; only the
 * accounts a term did *not* introduce are fetched one by one, which on an
 * unfiltered page is exactly what this read did before.
 *
 * **A failed or refused name is not a failed ledger.** Money rows are the point
 * of this screen, so a resolve that refuses leaves the name unset and the surface
 * renders the account id instead — visibly imperfect and still traversable, which
 * is what a blank column was not.
 */
export function useSubscriptions(currentPage: number, filters: LedgerFilters = EMPTY_FILTERS) {
  return useQuery({
    queryKey: subscriptionsKey(currentPage, filters),
    queryFn: async () => {
      const term = filters.term.trim();
      const matches = term ? await resolveTerm(term) : null;

      if (matches && matches.accounts.size === 0) return emptyLedgerPage(currentPage);

      const params = new URLSearchParams({
        current_page: String(currentPage),
        page_size: String(LEDGER_PAGE_SIZE),
      });
      if (filters.state) params.set('state', filters.state);
      if (filters.planId) params.set('plan_id', filters.planId);
      if (filters.source) params.set('source', filters.source);
      // Repeated, never comma-joined: the callee compiles the parameter into one
      // array bind, and a single joined string would arrive as one id nobody has.
      matches?.accounts.forEach((_person, id) => params.append('user_ids', id));

      const page = await api.get<OffsetPage<WireLedgerRow>>(
        'finance',
        `/admin/subscriptions?${params.toString()}`,
      );

      // One request per account the term did NOT already introduce, issued after
      // the ledger lands and never one per row: ten rows of one subscriber is one
      // call. `backend-auth` publishes no batch resolve on the operator surface,
      // so it is per account rather than per page, and the page size caps it.
      const unresolved = [...new Set(page.items.map((row) => row.user_id))].filter(
        (id) => !matches?.accounts.has(id),
      );
      const fetched = await Promise.all(
        unresolved.map(async (id) => {
          try {
            return [id, await api.get<LedgerSubscriber>('auth', `/admin/users/${id}`)] as const;
          } catch {
            return [id, null] as const;
          }
        }),
      );
      const byId = new Map<string, LedgerSubscriber | null>([...(matches?.accounts ?? []), ...fetched]);

      return {
        ...page,
        items: page.items.map((row) => {
          const person = byId.get(row.user_id) ?? null;
          return {
            ...row,
            // `||`, not `??`: an account with no name is served with an **empty**
            // `display_name` rather than a null one, and a nullish fallback let
            // that empty string through — the subscriber column rendered blank
            // and its link carried no accessible name at all. The id is visibly
            // imperfect and still traversable, which is what a blank cell is not.
            user_display_name: person?.display_name || row.user_id,
            user_contact_number: person?.phone_e164 ?? null,
          };
        }),
        matched_accounts: matches?.accounts.size ?? null,
        matches_truncated: matches?.truncated ?? false,
      } satisfies LedgerPage;
    },
    staleTime: STALE_TIME.list,
    // Narrowing the ledger — or walking a page of it — keeps the previous rows on
    // screen while the next read is in flight, so the table does not blink through
    // a skeleton between one term and the next.
    placeholderData: keepPreviousData,
  });
}

/** Resolve one term against the account register — ids and identity, never money. */
async function resolveTerm(
  term: string,
): Promise<{ accounts: Map<string, LedgerSubscriber>; truncated: boolean }> {
  const params = new URLSearchParams({ query: term, page_size: String(TERM_MATCH_CAP) });
  const found = await api.get<{ items: RosterRow[]; total: number }>(
    'auth',
    `/admin/users?${params.toString()}`,
  );

  return {
    accounts: new Map(
      found.items.map((person) => [
        person.id,
        { display_name: person.display_name, phone_e164: person.phone_e164 ?? null },
      ]),
    ),
    truncated: found.total > found.items.length,
  };
}

/**
 * The answer to a term nobody holds — an empty page shaped exactly like a served
 * one, so every reader downstream treats it the same way.
 *
 * It is built rather than requested because the request has no purpose: the money
 * service would be asked to narrow to an empty set, and «no rows» is already
 * known from the hop that just returned nothing.
 */
function emptyLedgerPage(currentPage: number): LedgerPage {
  return {
    items: [],
    current_page: currentPage,
    page_size: LEDGER_PAGE_SIZE,
    total: 0,
    has_next: false,
    matched_accounts: 0,
    matches_truncated: false,
  };
}

/**
 * The ledger page as this surface reads it: the service's envelope, plus what the
 * term resolve learned on the way.
 *
 * `matched_accounts` is `null` when no term was typed — «not asked» and «asked,
 * nobody matched» are different states, and the empty block says a different
 * thing for each.
 */
export interface LedgerPage extends OffsetPage<LedgerRow> {
  matched_accounts: number | null;
  matches_truncated: boolean;
}

/** What the ledger actually carries: the account's id, never its name. */
type WireLedgerRow = Omit<LedgerRow, 'user_display_name' | 'user_contact_number'>;

/** The two fields this surface reads off an account. Everything else is ignored. */
interface LedgerSubscriber {
  display_name: string;
  phone_e164?: string | null;
}

/** One roster row, as the account register serves it. Three fields are read; the rest are not. */
interface RosterRow extends LedgerSubscriber {
  id: string;
}
